import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import FinanceStagesEditor from './FinanceStagesEditor';
import pettyCashApi from '../../services/pettyCashApi';
import { MAPPED_REIMBURSE_METHODS, REIMBURSE_METHODS } from '../../types/pettyCash';
import type { AuxiliaryList, CashAccountOption, ImprestCheckMode, PettyCashProcessSettings, ReimburseAccountSetting, ReimburseMethod } from '../../types/pettyCash';
import { formatAmount, WEEKDAYS } from '../../utils/pettyCash';

/**
 * Petty Cash Setup › Settings, the sections of the imprest check, the reimbursement (target, weekend, SAP top-up
 * journal and the account each "Paid by" credits), the quarterly confirmation, the original receipts and the finance
 * stages.
 */

const methodLabel = (m: ReimburseMethod) => REIMBURSE_METHODS.find((x) => x.value === m)?.label || m;

interface Props {
  companyId: number;
  currency: string;
  /** The settings with their defaults filled in. */
  value: PettyCashProcessSettings;
  reviewRole: string;
  onChange: (patch: Partial<PettyCashProcessSettings>) => void;
  /** The tolerance as typed (a decimal, checked on save). */
  toleranceText: string;
  onToleranceText: (text: string) => void;
}

const digits = (text: string): number => Number(text.replace(/\D/g, '').slice(0, 3)) || 0;

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box>
    <Divider sx={{ mb: 2 }} />
    <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>{title}</Typography>
    {children}
  </Box>
);

const ProcessSettings: React.FC<Props> = ({ companyId, currency, value, reviewRole, onChange, toleranceText, onToleranceText }) => {
  const [accounts, setAccounts] = useState<CashAccountOption[] | null>(null);
  const [accountsError, setAccountsError] = useState<string | null>(null);

  // The bank account picker reads SAP's cash accounts (bank accounts are flagged as cash accounts too) once needed.
  useEffect(() => {
    if (!value.reimbursePost || accounts || !companyId) return undefined;
    let cancelled = false;
    pettyCashApi.cashAccounts(companyId)
      .then((rows) => { if (!cancelled) setAccounts(rows); })
      .catch((e: any) => { if (!cancelled) { setAccounts([]); setAccountsError(e?.response?.data?.message || 'The SAP cash accounts could not be read'); } });
    return () => { cancelled = true; };
  }, [value.reimbursePost, accounts, companyId]);

  // The auxiliaries (Dimension 5 codes) of each account a "Paid by" credits, read once per company and account.
  const [auxLists, setAuxLists] = useState<Record<string, AuxiliaryList | 'error'>>({});
  const requested = useRef<Set<string>>(new Set());
  const mapped = value.reimburseAccounts;
  const accountOf = (code: string) => accounts?.find((a) => a.code === code);
  const takesAux = (m: ReimburseAccountSetting): boolean => {
    const a = accountOf(m.account);
    return a ? !!(a.auxType || a.auxMandatory) : !!m.auxiliary;
  };
  const auxKey = (code: string) => `${companyId}|${code}`;
  const neededAux = Array.from(new Set(MAPPED_REIMBURSE_METHODS
    .map((m) => mapped[m])
    .filter((m): m is ReimburseAccountSetting => !!m && takesAux(m))
    .map((m) => m.account))).join(',');
  useEffect(() => {
    if (!value.reimbursePost || !companyId || accounts === null || !neededAux) return;
    for (const code of neededAux.split(',')) {
      const key = `${companyId}|${code}`;
      if (requested.current.has(key)) continue;
      requested.current.add(key);
      pettyCashApi.auxiliaries(companyId, { account: code })
        .then((list) => setAuxLists((s) => ({ ...s, [key]: list })))
        .catch(() => setAuxLists((s) => ({ ...s, [key]: 'error' })));
    }
  }, [value.reimbursePost, companyId, accounts, neededAux]);

  const setMapped = (method: ReimburseMethod, entry: ReimburseAccountSetting | null) => {
    const next = { ...value.reimburseAccounts };
    if (entry) next[method] = entry; else delete next[method];
    onChange({ reimburseAccounts: next });
  };

  const toggleWeekend = (day: number, on: boolean) => {
    const days = new Set(value.weekendDays);
    if (on) days.add(day); else days.delete(day);
    onChange({ weekendDays: Array.from(days).sort((a, b) => a - b) });
  };
  const bank = value.reimburseBankAccount;

  /** SAP's cash accounts as menu items, with the saved one when SAP no longer lists it. */
  const accountItems = (saved: string | null | undefined, savedName: string | null | undefined) => [
    ...(saved && accounts && !accounts.some((a) => a.code === saved)
      ? [<MenuItem key={`saved-${saved}`} value={saved}>{saved} {savedName || ''}</MenuItem>]
      : []),
    ...(accounts || []).map((a) => (
      <MenuItem key={a.code} value={a.code}>
        {a.code} {a.name}{a.balance !== null ? ` · balance ${formatAmount(a.balance)} ${currency}` : ''}
      </MenuItem>
    )),
  ];

  /** The auxiliary of the account a "Paid by" credits: the box the money comes from. */
  const auxiliaryField = (method: ReimburseMethod, m: ReimburseAccountSetting) => {
    const list = auxLists[auxKey(m.account)];
    const codes = list && list !== 'error' ? list.codes : [];
    const mandatory = !!accountOf(m.account)?.auxMandatory || (!!list && list !== 'error' && !!list.account?.auxMandatory);
    const help = list === undefined
      ? 'Reading the auxiliaries…'
      : list === 'error'
        ? 'The auxiliaries could not be read'
        : mandatory && !m.auxiliary ? 'Required: the box the money comes from' : 'The box the money comes from';
    return (
      <TextField
        id={`pc-set-reimb-${method}-aux`}
        select
        label={`${methodLabel(method)}: auxiliary`}
        value={m.auxiliary || ''}
        onChange={(e) => {
          const code = e.target.value;
          setMapped(method, { ...m, auxiliary: code || null, auxiliaryName: codes.find((c) => c.code === code)?.name ?? null });
        }}
        required={mandatory}
        error={list === 'error' || (mandatory && !m.auxiliary)}
        helperText={help}
        fullWidth
        SelectProps={{ displayEmpty: true }}
        InputLabelProps={{ shrink: true }}
      >
        <MenuItem value="" disabled={mandatory}><em>{mandatory ? 'Choose the box' : 'None'}</em></MenuItem>
        {m.auxiliary && !codes.some((c) => c.code === m.auxiliary)
          ? <MenuItem value={m.auxiliary}>{m.auxiliary} {m.auxiliaryName || ''}</MenuItem>
          : null}
        {codes.map((c) => <MenuItem key={c.code} value={c.code}>{c.code} {c.name}</MenuItem>)}
      </TextField>
    );
  };

  return (
    <>
      <Section title="Imprest check">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              id="pc-set-imprest"
              select
              label="Imprest check on claims"
              value={value.imprestCheck}
              onChange={(e) => onChange({ imprestCheck: e.target.value as ImprestCheckMode })}
              helperText={value.imprestCheck === 'block' ? 'A claim with a difference cannot be submitted' : value.imprestCheck === 'warn' ? 'Approvers see the difference' : 'No check'}
              fullWidth
            >
              <MenuItem value="off">Off</MenuItem>
              <MenuItem value="warn">Warn</MenuItem>
              <MenuItem value="block">Block</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              id="pc-set-imprest-tol"
              label={`Difference allowed ${currency}`.trim()}
              value={toleranceText}
              onChange={(e) => onToleranceText(e.target.value)}
              inputProps={{ inputMode: 'decimal' }}
              helperText="Between the cash counted and the expected cash"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              The holder&apos;s float, less the claims not yet reimbursed, the claim and the advances, gives the cash the holder should hold; the
              holder types the cash counted on each claim.
            </Typography>
          </Grid>
        </Grid>
      </Section>

      <Section title="Reimbursement">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              id="pc-set-reimb-days"
              label="Reimbursement target (working days)"
              value={String(value.reimburseTargetDays)}
              onChange={(e) => onChange({ reimburseTargetDays: digits(e.target.value) })}
              inputProps={{ inputMode: 'numeric' }}
              helperText="1 to 60, from the claim reaching finance; later claims show as overdue"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <FormControl component="fieldset" variant="standard">
              <FormLabel component="legend" sx={{ fontSize: 13, mb: 0.25 }}>Weekend days (not counted)</FormLabel>
              <FormGroup row>
                {WEEKDAYS.map((d) => (
                  <FormControlLabel
                    key={d.day}
                    control={<Checkbox size="small" checked={value.weekendDays.includes(d.day)} onChange={(e) => toggleWeekend(d.day, e.target.checked)} inputProps={{ 'aria-label': d.name }} />}
                    label={d.short}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={<Switch checked={value.reimbursePost} onChange={(e) => onChange({ reimbursePost: e.target.checked })} />}
              label="Post reimbursements in SAP"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              A top-up journal: debit the holder&apos;s cash account, credit the account of the way the holder was paid. Off: the reimbursement is only recorded.
            </Typography>
          </Grid>
          {value.reimbursePost ? (
            <Grid item xs={12} md={8}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Account credited for each &ldquo;Paid by&rdquo;</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Cash paid from another box credits that box: its cash account with its auxiliary. Cash, Cheque or Other left on
                &ldquo;Same as bank transfer&rdquo; credit the bank account.
              </Typography>
              {accounts === null ? (
                <Typography variant="body2" color="text.secondary"><CircularProgress size={14} sx={{ mr: 1 }} />Reading the SAP cash accounts…</Typography>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={7}>
                    <TextField
                      id="pc-set-reimb-bank"
                      select
                      label="Bank transfer"
                      value={bank || ''}
                      onChange={(e) => {
                        const a = accounts.find((x) => x.code === e.target.value);
                        onChange({ reimburseBankAccount: a?.code || null, reimburseBankAccountName: a?.name || null });
                      }}
                      error={!!accountsError || !bank}
                      helperText={accountsError || (bank ? 'The bank account the transfers are paid from' : 'Required to post the top-ups')}
                      fullWidth
                      required
                      SelectProps={{ displayEmpty: true }}
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value=""><em>Not set</em></MenuItem>
                      {accountItems(bank, value.reimburseBankAccountName)}
                    </TextField>
                  </Grid>
                  <Grid item md={5} sx={{ display: { xs: 'none', md: 'block' } }} />
                  {MAPPED_REIMBURSE_METHODS.map((method) => {
                    const m = mapped[method];
                    return (
                      <React.Fragment key={method}>
                        <Grid item xs={12} md={7}>
                          <TextField
                            id={`pc-set-reimb-${method}`}
                            select
                            label={methodLabel(method)}
                            value={m?.account || ''}
                            onChange={(e) => {
                              const code = e.target.value;
                              const a = accounts.find((x) => x.code === code);
                              setMapped(method, code ? { account: code, accountName: a?.name ?? null, auxiliary: null, auxiliaryName: null } : null);
                            }}
                            helperText={m ? 'Its own account' : 'Credits the bank account'}
                            fullWidth
                            SelectProps={{ displayEmpty: true }}
                            InputLabelProps={{ shrink: true }}
                          >
                            <MenuItem value=""><em>Same as bank transfer</em></MenuItem>
                            {accountItems(m?.account, m?.accountName)}
                          </TextField>
                        </Grid>
                        {m && takesAux(m) ? <Grid item xs={12} md={5}>{auxiliaryField(method, m)}</Grid> : null}
                      </React.Fragment>
                    );
                  })}
                </Grid>
              )}
            </Grid>
          ) : null}
        </Grid>
      </Section>

      <Section title="Quarterly confirmation">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControlLabel
              control={<Switch checked={value.quarterlyConfirm} onChange={(e) => onChange({ quarterlyConfirm: e.target.checked })} />}
              label="Holders confirm their cash every quarter"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              id="pc-set-confirm-days"
              label="Days around the quarter end"
              value={String(value.confirmWindowDays)}
              onChange={(e) => onChange({ confirmWindowDays: digits(e.target.value) })}
              inputProps={{ inputMode: 'numeric' }}
              helperText="1 to 45: the confirmation is due from this many days before the quarter end to as many after"
              disabled={!value.quarterlyConfirm}
              fullWidth
            />
          </Grid>
        </Grid>
      </Section>

      <Section title="Original receipts">
        <FormControlLabel
          control={<Switch checked={value.requireOriginals} onChange={(e) => onChange({ requireOriginals: e.target.checked })} />}
          label="Original receipts required before the last finance approval"
        />
        <Typography variant="caption" color="text.secondary" display="block">
          Finance marks the paper receipts received on the claim; until then the last finance stage cannot approve it.
        </Typography>
      </Section>

      <Section title="Approval after the PR flow">
        <FinanceStagesEditor reviewRole={reviewRole} stages={value.financeStages} onChange={(financeStages) => onChange({ financeStages })} />
      </Section>
    </>
  );
};

export default ProcessSettings;
